import React, { useEffect, useState, useCallback } from "react";
import { ZoomInOutlined } from "@ant-design/icons";
import * as pdfjs from "pdfjs-dist/legacy/build/pdf";
import { PDFDocumentProxy } from "pdfjs-dist/legacy/build/pdf";
import { useTranslation } from "react-i18next";

import { Position } from "../../interfaces";
import {
  calculateImageV2,
  getOutline,
  loadProgress,
  round,
  updateProgress,
} from "../../utils";
import { API, Storage } from "aws-amplify";
import config from "../../aws-exports";
import ImagePreviewLoading from "../ImagePreviewLoading";
import { Loader, Text } from "@mantine/core";
import ImageOutline from "../image/ImageOutline";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.js",
  import.meta.url
).href;

type Props = {
  url: string;
  image?: File;
  base64?: string;
  item: Position;
  outline: string | undefined;
  setPdfData: any;
  previewPdf?: (imgUrl?: string) => void;
  setOutline?: (outline: string) => void;
};

let request: XMLHttpRequest | null = null;

const PdfPreview: React.FC<Props> = ({
  image,
  url,
  base64,
  item,
  outline,
  setPdfData,
  previewPdf,
  setOutline,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [error] = useState<boolean>(false);
  const [statusMsg, setStatusMsg] = useState<string>(
    "tooLargeConvertingToImage"
  );
  const [pdfRef, setPdfRef] = useState<PDFDocumentProxy | null>();
  const setIsLarge = useState<boolean>(false)[1];
  const [currentUrl, setCurrentUrl] = useState<string | null>();

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setOutline && setOutline(getOutline({ image: e.currentTarget, item }));
  };

  const renderPage = useCallback(
    async (pageNum: number) => {
      pdfRef?.getPage(pageNum).then(async (page) => {
        setLoading(true);
                const viewport = page.getViewport({ scale: 1 });
        const width = round(viewport.width, 0);
        const height = round(viewport.height, 0);

        const { _ratio, _dpi } = calculateImageV2({ width, height, item });

        setPdfData({ _ratio, _dpi, _numPages: pdfRef.numPages });

        if (image && base64) {
          const fileName: string = `${Date.now()}-${image?.name}`;
          const region = config.aws_user_files_s3_bucket_region;
          const bucketName = config.aws_user_files_s3_bucket;
          const fileType = image?.type;

          
          const signedUrl = await API.get(
            "s3UploadApi",
            `/?fileType=${fileType}&fileName=public/${fileName}&region=${region}&bucketName=${bucketName}`,
            {}
          );

          request = new XMLHttpRequest();
          request.responseType = "json";

          const progress = document.getElementById(
            "pdf-upload-progress"
          ) as HTMLElement;

          request.upload.addEventListener("progress", (e: any) => {
            updateProgress({ progress, e });
          });

          request.addEventListener("load", async () => {
            
            if (request?.status === 200) {
              setPdfData({ _key: fileName });
              progress.innerText = "0%";
              setStatusMsg("generatingImagePreview");
              await new Promise(() => {
                const callback = async () => {
                  const timerId = setInterval(async () => {
                    
                    const signedUrl = await Storage.get(`${fileName}.jpg`);
                    const res = await fetch(signedUrl);

                    if (res.status === 200) {
                                            setCurrentUrl(signedUrl);
                      setLoading(false);
                      clearInterval(timerId);
                    }
                  }, 300);
                };
                const download = ((image.size / 1000 / 1000) * 3) / 200; // 3 seconds for 200mb file
                const duration = (4 + download) * 1000;
                loadProgress(progress, duration, callback);
              });
              request = null;
            } else {
              console.error("Error in upload.");
              request = null;
            }
          });

          request.addEventListener("error", (e: any) => {
            console.error("HTTP request error: ", e);
            request = null;
          });

          request.open("put", signedUrl);
          request.send(image);

          return;
        }

        // render Pdf
                const canvasPdf = document.createElement("canvas");
        canvasPdf.height = height;
        canvasPdf.width = width;

        const viewpointScaleOne = page.getViewport({ scale: 1 });
        const scale = Math.max(
          height / viewpointScaleOne.height,
          width / viewpointScaleOne.width
        );
        const scaledViewport = page.getViewport({ scale });

        const renderContext: any = {
          canvasContext: canvasPdf.getContext("2d"),
          viewport: { ...scaledViewport, height, width },
        };

        await page.render(renderContext).promise;

        setTimeout(() => {
          const imgUrl = canvasPdf.toDataURL();
                    setCurrentUrl(imgUrl);
          setLoading(false);
          setPdfData({ _ratio, _dpi, _numPages: pdfRef.numPages });
        }, 300);
      });
    },
    [pdfRef]
  );

  const loadDocument = async () => {
    
    try {
      const loadedPdf = await pdfjs.getDocument(url).promise;
            setPdfRef(loadedPdf);
    } catch (err) {
      console.error("Error loading PDF document: ", err);
    }
  };

  useEffect(() => {
    renderPage(1);
  }, [pdfRef, renderPage]);

  useEffect(() => {
        setLoading(true);
    setIsLarge((image && image?.size / 1024 / 1024 > 100) || false);
    loadDocument();

    return () => {
            URL.revokeObjectURL(url);
      if (request) {
                request.abort();
      }
    };
  }, []);

  return (
    <>
      <div className="image-wrapper">
        {outline && (
          <div className="image-wrapper__info">
            <div className="marker-line" />
            <Text color="#fa00ff" weight={700}>
              {t("cutting")}
            </Text>
          </div>
        )}
        <div className="image-container">
          <ImageOutline outline={outline} />
          <img
            id="image-preview_pdf"
            className="image-preview"
            style={{
              visibility: currentUrl ? "visible" : "hidden",
              display: currentUrl ? "block" : "none",
            }}
            src={currentUrl || ""}
            onLoad={onImageLoad}
            onClick={() => currentUrl && previewPdf && previewPdf(currentUrl)}
            alt=""
          />
        </div>
      </div>
      <div className="pdf-container">
        {loading && (
          <>
            <div className="row row-center row-column">
              <span className="text-center" style={{ fontSize: 18 }}>
                {t(statusMsg)}
              </span>
              <p className="upload-status__progress">
                <Loader size={28} />
                <span id="pdf-upload-progress">0%</span>
              </p>
            </div>
          </>
        )}
        {loading && <ImagePreviewLoading />}
        {!loading && !error && currentUrl && (
          <>
            <div
              onClick={() => currentUrl && previewPdf && previewPdf(currentUrl)}
              className="image-action"
            >
              <span>{t("imageFullScreen")}</span>
              <ZoomInOutlined style={{ fontSize: 28, marginLeft: 5 }} />
            </div>
            <span className="text-small">{t("imagePreviewDescription")}</span>
          </>
        )}
      </div>
    </>
  );
};

export default React.memo(PdfPreview);
